/**
 * Keeping a device's trips and the account's trips the same trips.
 *
 * Pure functions, no storage and no network: the website (localStorage) and
 * the app (AsyncStorage) each wrap these with their own storage and their own
 * API client, so the rules below are one set of rules on both.
 *
 * ## The ledger
 *
 * For every trip the device and the account last agreed on, the device keeps a
 * fingerprint of that agreed copy. It is what lets a device tell apart:
 *
 * - "I changed this trip since" (fingerprint differs): send it up.
 * - "I have not touched it" (fingerprint matches): take the account's copy.
 * - "I deleted it here" (in the ledger, gone from the device): delete it there.
 * - "It was deleted elsewhere" (in the ledger, gone from the account, and not
 *   touched here): drop it here too.
 * - "It is new on another device" (on the account, never in the ledger): add it.
 *
 * Conflicts (edited here and elsewhere since the last agreement) resolve to
 * this device's copy, because it is the one the guest is looking at.
 */
import type { Trip } from "../types";

/** Trip id -> fingerprint of the copy last agreed with the account. */
export type TripLedger = Readonly<Record<string, string>>;

/** Who the device's trips belong to, and what was last agreed with them. */
export interface TripAccountState {
  /** The account the on-device trips mirror; null while they are a guest's. */
  readonly owner: string | null;
  readonly ledger: TripLedger;
  /**
   * Trips the guest planned before signing in and chose not to save ("Not
   * now"). They stay on this device, visible and editable, but are never sent
   * to the account and never taken from it. Cleared on sign-out, when they
   * become the guest's own trips again (and are offered at the next sign-in).
   */
  readonly deviceOnly?: readonly string[];
}

export const EMPTY_TRIP_ACCOUNT_STATE: TripAccountState = { owner: null, ledger: {} };

/** Read stored bookkeeping defensively: anything malformed reads as a guest's. */
export function parseTripAccountState(raw: unknown): TripAccountState {
  if (!raw || typeof raw !== "object") return EMPTY_TRIP_ACCOUNT_STATE;
  const parsed = raw as Partial<Record<keyof TripAccountState, unknown>>;
  const owner = typeof parsed.owner === "string" ? parsed.owner : null;
  const ledger =
    parsed.ledger && typeof parsed.ledger === "object" && !Array.isArray(parsed.ledger)
      ? (parsed.ledger as Record<string, string>)
      : {};
  const deviceOnly = Array.isArray(parsed.deviceOnly)
    ? parsed.deviceOnly.filter((id): id is string => typeof id === "string")
    : [];
  return deviceOnly.length > 0 ? { owner, ledger, deviceOnly } : { owner, ledger };
}

function stable(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "null";
  if (Array.isArray(value)) return `[${value.map(stable).join(",")}]`;
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj)
    .filter((k) => obj[k] !== undefined)
    .sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stable(obj[k])}`).join(",")}}`;
}

/**
 * A short, order-independent fingerprint of a trip. Key order and `undefined`
 * fields do not count, so the same trip read back from the server (which drops
 * undefined and may reorder keys) matches the device's copy.
 */
export function tripFingerprint(trip: Trip): string {
  const s = stable(trip);
  // FNV-1a, two lanes, enough to tell edits apart; this is not security.
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < s.length; i += 1) {
    const c = s.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
    h2 = Math.imul(h2 ^ c, 0x811c9dc5) >>> 0;
  }
  return `${s.length.toString(36)}.${h1.toString(36)}.${h2.toString(36)}`;
}

export interface TripReconcileResult {
  /** The device's trips after taking the account's news. */
  readonly trips: Trip[];
  /** Trips to send up (new or changed here). */
  readonly upload: Trip[];
  /** Trip ids to delete on the account (deleted here). */
  readonly remove: string[];
  /**
   * The ledger for everything already agreed. Uploads and removals are left
   * out: the caller records them once the account has confirmed each one, so
   * a failed request is simply tried again next time.
   */
  readonly ledger: Record<string, string>;
}

/** Merge the account's trips into the device's. See the file note for the rules. */
export function reconcileTrips(
  local: readonly Trip[],
  remote: readonly Trip[],
  ledger: TripLedger,
): TripReconcileResult {
  const remoteById = new Map(remote.map((t) => [t.id, t]));
  const localIds = new Set(local.map((t) => t.id));
  const trips: Trip[] = [];
  const upload: Trip[] = [];
  const remove: string[] = [];
  const next: Record<string, string> = {};

  for (const mine of local) {
    const fp = tripFingerprint(mine);
    const agreed = ledger[mine.id];
    const theirs = remoteById.get(mine.id);
    const touchedHere = agreed === undefined || agreed !== fp;

    if (theirs) {
      const theirFp = tripFingerprint(theirs);
      if (theirFp === fp) {
        trips.push(mine);
        next[mine.id] = fp;
      } else if (touchedHere) {
        trips.push(mine);
        upload.push(mine);
      } else {
        trips.push(theirs);
        next[mine.id] = theirFp;
      }
      continue;
    }

    if (agreed !== undefined && !touchedHere) {
      // Agreed before, untouched here, gone from the account: deleted on
      // another device. Drop it here too.
      continue;
    }
    // New here, or edited here after being deleted elsewhere: keep and send.
    trips.push(mine);
    upload.push(mine);
  }

  for (const theirs of remote) {
    if (localIds.has(theirs.id)) continue;
    if (ledger[theirs.id] !== undefined) {
      // Agreed before and deleted on this device since.
      remove.push(theirs.id);
      continue;
    }
    trips.push(theirs);
    next[theirs.id] = tripFingerprint(theirs);
  }

  return { trips, upload, remove, ledger: next };
}

/**
 * What changed on the device since the last agreement, without asking the
 * account: for pushing edits as the guest makes them.
 */
export function pendingTripChanges(
  local: readonly Trip[],
  ledger: TripLedger,
): { upload: Trip[]; remove: string[] } {
  const ids = new Set(local.map((t) => t.id));
  const upload = local.filter((t) => ledger[t.id] !== tripFingerprint(t));
  const remove = Object.keys(ledger).filter((id) => !ids.has(id));
  return { upload, remove };
}

export type TripSignInPlan =
  /** The device already mirrors this account: just sync. */
  | { readonly kind: "resume" }
  /** Trips made while signed out: ask whether to keep them on the account. */
  | { readonly kind: "offer"; readonly trips: Trip[] }
  /** Nothing of the guest's to keep: take the account's trips as they are. */
  | { readonly kind: "fresh" };

/**
 * What to do when a guest signs in on this device.
 *
 * Trips left over from a different account are never offered to this one:
 * they are that person's, so they are dropped from the screen, not merged.
 */
export function planTripSignIn(
  local: readonly Trip[],
  state: TripAccountState,
  userId: string,
): TripSignInPlan {
  if (state.owner === userId) return { kind: "resume" };
  if (state.owner !== null) return { kind: "fresh" };
  return local.length > 0 ? { kind: "offer", trips: [...local] } : { kind: "fresh" };
}
